/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { useState, useEffect, useCallback } from 'react';
import { useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Transaction, getTransactionHistory } from '../../utils/wallet-helpers';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
	ChevronLeft,
	ChevronRight,
	ExternalLink,
	RefreshCw,
	Check,
	X,
	ArrowUpRight,
	ArrowDownLeft,
	Coins,
	Flame,
	Image,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import { useWeb3Auth } from '@/providers/Web3AuthProvider';
import { getMemberAuthToken } from '@/lib/member-wallet-rpc';
import { toast } from '@/components/ui/use-toast';

const ALL_TRANSACTION_TYPES: Array<'eth' | 'karma' | 'kusd' | 'nft'> = [
	'eth',
	'karma',
	'kusd',
	'nft',
];

const NFT_SYMBOLS = new Set([
	'GAME ID',
	'GAME ID (POLY)',
	'BUILDER',
	'ACCESS',
	'ORACLE',
	'PLAYER',
	'NFT',
]);

interface TransactionHistoryProps {
	walletAddress: string;
	className?: string;
}

export const TransactionHistory: React.FC<TransactionHistoryProps> = ({
	walletAddress,
	className = '',
}) => {
	const { memberSigner: memberAccount } = useWeb3Auth();
	const fetchScanner = useAction(api.walletTransactions.fetchScanner);
	const fetchChainTransactions = useAction(
		api.walletTransactions.fetchChainTransactions
	);
	const [transactions, setTransactions] = useState<Transaction[]>([]);
	const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [hasMoreUiPages, setHasMoreUiPages] = useState(false);
	const [showKarma, setShowKarma] = useState(true);
	const [showKusd, setShowKusd] = useState(true);
	const [showNft, setShowNft] = useState(true);
	const pageSize = 20;

	const getStorageKey = useCallback(
		(address: string) => `tx_history_${address.toLowerCase()}`,
		[]
	);

	const loadFromCache = useCallback((): Transaction[] | null => {
		if (!walletAddress) return null;
		try {
			const cached = sessionStorage.getItem(getStorageKey(walletAddress));
			if (!cached) return null;
			const data = JSON.parse(cached);
			if (
				Array.isArray(data?.transactions) &&
				data.transactions.length > 0 &&
				Date.now() - Number(data.timestamp || 0) < 5 * 60 * 1000
			) {
				return data.transactions;
			}
		} catch (error) {
			console.error('Error loading transaction cache:', error);
		}
		return null;
	}, [getStorageKey, walletAddress]);

	const saveToCache = useCallback(
		(nextTransactions: Transaction[]) => {
			if (!walletAddress) return;
			try {
				sessionStorage.setItem(
					getStorageKey(walletAddress),
					JSON.stringify({
						transactions: nextTransactions,
						timestamp: Date.now(),
					})
				);
			} catch (error) {
				console.error('Error saving transaction cache:', error);
			}
		},
		[getStorageKey, walletAddress]
	);

	const getFilteredTransactions = useCallback(
		(sourceTransactions: Transaction[]) => {
			if (!showKarma && !showKusd && !showNft) return [];

			return sourceTransactions.filter((tx) => {
				const tokenSymbol = tx.tokenSymbol?.toUpperCase() || '';
				const tokenName = tx.tokenName || '';
				const tokenDecimals = parseInt(tx.tokenDecimals || '18');
				const isKarma = tokenSymbol === 'KARMA' || tokenSymbol === 'WKRM';
				const isKusd = tokenSymbol === 'KUSD';
				const isNft =
					NFT_SYMBOLS.has(tokenSymbol) ||
					tokenName.includes('NFT') ||
					tx.tokenDecimals === '0' ||
					tokenDecimals === 0;

				return (
					(showKarma && isKarma) ||
					(showKusd && isKusd) ||
					(showNft && isNft)
				);
			});
		},
		[showKarma, showKusd, showNft]
	);

	const applyFilters = useCallback(
		(sourceTransactions: Transaction[] = allTransactions) => {
			const filteredTransactions = getFilteredTransactions(sourceTransactions);
			const newCurrentPage = 1;
			setCurrentPage(newCurrentPage);
			setTransactions(filteredTransactions.slice(0, newCurrentPage * pageSize));
			setHasMoreUiPages(
				newCurrentPage * pageSize < filteredTransactions.length
			);
		},
		[allTransactions, getFilteredTransactions, pageSize]
	);

	const fetchAllTransactions = useCallback(
		async (refresh: boolean = false) => {
			if (!walletAddress) return;

			if (!refresh) {
				const cachedTransactions = loadFromCache();
				if (cachedTransactions) {
					setAllTransactions(cachedTransactions);
					return;
				}
			}

			setIsLoading(true);
			if (refresh) setIsRefreshing(true);

			try {
				const { result: allFetchedSortedTransactions } =
					await getTransactionHistory(
						walletAddress,
						ALL_TRANSACTION_TYPES,
						{
							refresh,
							scannerFetch: fetchScanner,
							chainTransactionFetch: async request => {
								if (!memberAccount) throw new Error('MEMBER_SESSION_UNAVAILABLE');
								return fetchChainTransactions({ ...request, authToken: await getMemberAuthToken(memberAccount) });
							},
						}
					);

				saveToCache(allFetchedSortedTransactions);
				setAllTransactions(allFetchedSortedTransactions);
			} catch (error) {
				console.error('Error fetching transactions:', error);
				toast({
					title: 'Failed to load transactions',
					description: 'Could not fetch transaction history right now',
					variant: 'destructive',
				});
			} finally {
				setIsLoading(false);
				setIsRefreshing(false);
			}
		},
		[
			walletAddress,
			loadFromCache,
			saveToCache,
			fetchScanner,
			fetchChainTransactions,
			memberAccount,
		]
	);

	useEffect(() => {
		if (!walletAddress) {
			setAllTransactions([]);
			setTransactions([]);
			setCurrentPage(1);
			setHasMoreUiPages(false);
			setIsLoading(false);
			setIsRefreshing(false);
			return;
		}
		setAllTransactions([]);
		setTransactions([]);
		setCurrentPage(1);
		setHasMoreUiPages(false);
		fetchAllTransactions(false);
	}, [walletAddress, fetchAllTransactions]);

	useEffect(() => {
		applyFilters();
	}, [applyFilters]);

	const handleRefresh = () => {
		fetchAllTransactions(true);
	};

	const paginateTo = (newPage: number) => {
		setCurrentPage(newPage);
		const filteredTransactions = getFilteredTransactions(allTransactions);
		const paginatedTxns = filteredTransactions.slice(
			(newPage - 1) * pageSize,
			newPage * pageSize
		);
		setTransactions(paginatedTxns);
		setHasMoreUiPages(newPage * pageSize < filteredTransactions.length);
	};

	const handleNextPage = () => {
		if (hasMoreUiPages) {
			paginateTo(currentPage + 1);
		}
	};

	const handlePrevPage = () => {
		if (currentPage > 1) {
			paginateTo(currentPage - 1);
		}
	};

	const formatValue = (
		value: string,
		tokenSymbol?: string,
		tokenDecimals?: string,
		tokenName?: string,
		tokenID?: string
	) => {
		const decimals = tokenDecimals ? parseInt(tokenDecimals) : 18;
		const divisor = 10 ** decimals;

		// Handle NFT transactions - use tokenID if available
		const symbol = tokenSymbol?.toUpperCase() || '';
		if (NFT_SYMBOLS.has(symbol) || tokenName?.includes('NFT') || decimals === 0) {
			return tokenID ? `Token ID: ${tokenID}` : `Token ID: ${value}`;
		}

		if (!tokenSymbol || tokenSymbol.toUpperCase() === 'ETH') {
			const ether = parseFloat(value) / 1e18;
			return `${ether.toFixed(6)} ETH`;
		}

		const tokenValue = parseFloat(value) / divisor;
		return `${tokenValue.toFixed(tokenValue < 0.01 && decimals > 2 ? Math.min(decimals, 6) : 2)} ${tokenSymbol}`;
	};

	const formatDate = (timestamp: string) => {
		const date = new Date(parseInt(timestamp) * 1000);
		return date.toLocaleString(undefined, {
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
		});
	};

	const getTransactionTypeLabel = (tx: Transaction) => {
		const isSender = tx.from.toLowerCase() === walletAddress.toLowerCase();

		if (isSender) {
			return (
				<Badge className='bg-amber-500/80 text-white px-2 py-1 text-xs font-semibold flex items-center gap-1'>
					<ArrowUpRight className='h-3 w-3' />
					SENT
				</Badge>
			);
		} else {
			return (
				<Badge className='bg-emerald-500/80 text-white px-2 py-1 text-xs font-semibold flex items-center gap-1'>
					<ArrowDownLeft className='h-3 w-3' />
					RECEIVED
				</Badge>
			);
		}
	};

	const renderTransactionStatus = (tx: Transaction) => {
		if (tx.isError === '1') {
			return (
				<Badge
					variant='destructive'
					className='flex items-center gap-1'
				>
					<X className='h-3 w-3' />
					Failed
				</Badge>
			);
		}

		if (parseInt(tx.confirmations) > 12) {
			return (
				<Badge
					variant='outline'
					className='bg-emerald-500/20 text-emerald-400 border-emerald-800/50 flex items-center gap-1'
				>
					<Check className='h-3 w-3' />
					Confirmed
				</Badge>
			);
		}

		return (
			<Badge
				variant='outline'
				className='bg-amber-500/20 text-amber-400 border-amber-800/50 flex items-center gap-1'
			>
				<RefreshCw className='h-3 w-3 animate-spin' />
				Pending
			</Badge>
		);
	};

	const getExplorerTxUrl = (tx: Transaction) => {
		if (tx.network === 'base') return `https://basescan.org/tx/${tx.hash}`;
		if (tx.network === 'polygon') return `https://polygonscan.com/tx/${tx.hash}`;
		return `https://etherscan.io/tx/${tx.hash}`;
	};

	const getNativeTokenLabel = (tx: Transaction) => {
		if (tx.network === 'polygon') return 'MATIC';
		return 'ETH';
	};

	return (
		<Card
			className={`border-primary/20 dark:border-pink-900/50 dark:bg-gradient-to-b dark:from-zinc-900 dark:to-black bg-white/80 shadow-md overflow-hidden ${className}`}
		>
			<CardContent className='p-4'>
				<div className='flex justify-between items-center mb-4'>
					<h3 className='text-xl font-bold text-white border-b border-pink-800/30 pb-2'>
						Recent Transactions
					</h3>
					<Button
						variant='ghost'
						size='sm'
						className='text-pink-300 hover:text-pink-100 hover:bg-pink-800/30 transition-all duration-200'
						onClick={handleRefresh}
						disabled={isRefreshing || isLoading}
					>
						<RefreshCw
							className={`h-4 w-4 mr-1 ${isRefreshing || (isLoading && transactions.length === 0) ? 'animate-spin' : ''}`}
						/>
						{isRefreshing ||
						(isLoading && transactions.length === 0)
							? 'Updating...'
							: 'Refresh'}
					</Button>
				</div>

				<div className='flex space-x-2 mb-4'>
					<Button
						onClick={() => setShowKarma(!showKarma)}
						variant={showKarma ? 'default' : 'outline'}
						size='sm'
						className={`px-3 py-1 text-xs font-semibold rounded-full transition-all duration-200 flex items-center space-x-1 shadow-md hover:shadow-lg transform hover:scale-105
							${
								showKarma
									? 'bg-gradient-to-r from-orange-500 to-red-600 text-white border-transparent'
									: 'bg-zinc-700/30 text-zinc-300 border-zinc-600 hover:bg-zinc-600/50 hover:text-white'
							}`}
					>
						<Flame
							className={`h-3.5 w-3.5 ${showKarma ? 'text-white' : 'text-orange-400'}`}
						/>
						<span>KARMA/WKRM</span>
					</Button>
					<Button
						onClick={() => setShowKusd(!showKusd)}
						variant={showKusd ? 'default' : 'outline'}
						size='sm'
						className={`px-3 py-1 text-xs font-semibold rounded-full transition-all duration-200 flex items-center space-x-1 shadow-md hover:shadow-lg transform hover:scale-105
							${
								showKusd
									? 'bg-gradient-to-r from-sky-500 to-indigo-600 text-white border-transparent'
									: 'bg-zinc-700/30 text-zinc-300 border-zinc-600 hover:bg-zinc-600/50 hover:text-white'
							}`}
					>
						<Coins
							className={`h-3.5 w-3.5 ${showKusd ? 'text-white' : 'text-sky-400'}`}
						/>
						<span>KUSD</span>
					</Button>
					<Button
						onClick={() => setShowNft(!showNft)}
						variant={showNft ? 'default' : 'outline'}
						size='sm'
						className={`px-3 py-1 text-xs font-semibold rounded-full transition-all duration-200 flex items-center space-x-1 shadow-md hover:shadow-lg transform hover:scale-105
							${
								showNft
									? 'bg-gradient-to-r from-green-500 to-lime-600 text-white border-transparent'
									: 'bg-zinc-700/30 text-zinc-300 border-zinc-600 hover:bg-zinc-600/50 hover:text-white'
							}`}
					>
						<Image
							className={`h-3.5 w-3.5 ${showNft ? 'text-white' : 'text-green-400'}`}
						/>
						<span>NFT</span>
					</Button>
				</div>

				{isLoading && transactions.length === 0 && !isRefreshing ? (
					<div className='space-y-3'>
						{[...Array(5)].map((_, index) => (
							<div
								key={index}
								className='flex flex-col p-3 rounded-lg bg-gradient-to-br from-zinc-800/40 to-zinc-900/40 border border-pink-900/20'
							>
								<div className='flex items-center justify-between mb-2'>
									<Skeleton className='h-5 w-24 bg-zinc-700/40' />
									<Skeleton className='h-5 w-28 bg-zinc-700/40' />
								</div>
								<div className='flex items-center justify-between'>
									<Skeleton className='h-4 w-32 bg-zinc-700/40' />
									<Skeleton className='h-4 w-20 bg-zinc-700/40' />
								</div>
							</div>
						))}
					</div>
				) : transactions.length === 0 && !isLoading ? (
					<div className='flex flex-col items-center justify-center p-6 rounded-lg bg-gradient-to-br from-zinc-800/40 to-zinc-900/40 border border-pink-900/20 text-sm h-[500px]'>
						<p className='text-gray-400'>
							No transactions found for the selected filters.
						</p>
					</div>
				) : (
					<div className='h-[500px] overflow-y-auto pr-2 space-y-1'>
						{/* Scrollable Transaction List */}
						<div className='space-y-3 px-1'>
							<AnimatePresence initial={false}>
								{transactions.map((tx, index) => (
									<motion.div
										key={`${tx.hash}-${tx.network}-${index}`}
										initial={{ opacity: 0, y: 20 }}
										animate={{ opacity: 1, y: 0 }}
										exit={{ opacity: 0, x: -100 }}
										transition={{
											duration: 0.3,
											delay: index * 0.05,
											ease: 'easeOut',
										}}
										className='flex flex-col p-3 rounded-lg bg-gradient-to-br from-zinc-800/40 to-zinc-900/40 border border-pink-900/20 text-sm hover:border-pink-600/40 hover:from-zinc-800/60 hover:shadow-[0_0_10px_rgba(236,72,153,0.15)] transition-all duration-200 group'
									>
										<div className='flex items-center justify-between mb-1'>
											<div className='flex items-center space-x-2'>
												{getTransactionTypeLabel(tx)}
												<span className='text-white/70 mr-1'>
													{tx.tokenSymbol ||
														getNativeTokenLabel(
															tx
														)}
												</span>

												{[
													'KARMA',
													'WKRM',
												].includes(
													tx.tokenSymbol?.toUpperCase() ||
														''
												) && (
													<Badge className='text-xs font-semibold bg-gradient-to-r from-orange-500 to-red-600 text-white border-transparent px-1.5 py-0.5'>
														KARMA
													</Badge>
												)}
												{tx.tokenSymbol?.toUpperCase() ===
													'KUSD' && (
													<Badge className='text-xs font-semibold bg-gradient-to-r from-sky-500 to-indigo-600 text-white border-transparent px-1.5 py-0.5'>
														KUSD
													</Badge>
												)}

												{/* NFT Transaction Badge */}
												{(tx.tokenSymbol?.toUpperCase() ===
													'NFT' ||
													tx.tokenName?.includes(
														'NFT'
													) ||
													tx.tokenDecimals ===
														'0') && (
													<Badge className='text-xs font-semibold bg-gradient-to-r from-green-500 to-lime-600 text-white border-transparent px-1.5 py-0.5'>
														NFT
													</Badge>
												)}

												{tx.network === 'polygon' &&
													(!tx.tokenSymbol ||
														(tx.tokenSymbol.toUpperCase() !==
															'KUSD' &&
															tx.tokenSymbol.toUpperCase() !==
																'NFT')) && (
														<Badge
															variant='outline'
															className='text-xs bg-purple-600/20 text-purple-300 border-purple-700 px-1.5 py-0.5'
														>
															{tx.tokenSymbol
																? 'Polygon'
																: 'MATIC'}
														</Badge>
													)}
												{tx.network === 'ethereum' &&
													(!tx.tokenSymbol ||
														(tx.tokenSymbol.toUpperCase() !==
															'KARMA' &&
															tx.tokenSymbol.toUpperCase() !==
																'NFT')) && (
														<Badge
															variant='outline'
															className='text-xs bg-blue-600/20 text-blue-300 border-blue-700 px-1.5 py-0.5'
														>
															{tx.tokenSymbol
																? 'Ethereum'
															: 'Ethereum'}
														</Badge>
													)}
												{tx.network === 'base' &&
													(!tx.tokenSymbol ||
														tx.tokenSymbol.toUpperCase() !==
															'WKRM') && (
														<Badge
															variant='outline'
															className='text-xs bg-sky-600/20 text-sky-300 border-sky-700 px-1.5 py-0.5'
														>
															Base
														</Badge>
													)}
											</div>
											<span className='text-gray-400 text-xs'>
												{formatDate(tx.timeStamp)}
											</span>
										</div>

										<div className='flex items-center justify-between mt-1'>
											<a
												href={getExplorerTxUrl(tx)}
												target='_blank'
												rel='noopener noreferrer'
												className='text-pink-400 hover:text-pink-300 flex items-center truncate transition-colors duration-200 group-hover:underline'
											>
												<span className='truncate max-w-[170px] font-mono text-xs'>
													{tx.hash.substring(0, 10)}
													...
													{tx.hash.substring(
														tx.hash.length - 8
													)}
												</span>
												<ExternalLink className='h-3 w-3 ml-1 inline-flex opacity-0 group-hover:opacity-100 transition-opacity' />
											</a>
											<div className='flex items-center space-x-2'>
												<span className='text-white/80 font-medium text-xs'>
													{formatValue(
														tx.value,
														tx.tokenSymbol,
														tx.tokenDecimals,
														tx.tokenName,
														tx.tokenID
													)}
												</span>
												{renderTransactionStatus(tx)}
											</div>
										</div>
									</motion.div>
								))}
							</AnimatePresence>

							{/* Load More Button at bottom of scroll area */}
							{hasMoreUiPages && (
								<div className='flex justify-center py-4'>
									<Button
										variant='outline'
										size='sm'
										onClick={handleNextPage}
										disabled={isLoading}
										className='text-pink-300 hover:text-white border-pink-900/50 hover:bg-pink-800/30'
									>
										{isLoading ? (
											<RefreshCw className='h-4 w-4 mr-2 animate-spin' />
										) : (
											<ChevronRight className='h-4 w-4 mr-2' />
										)}
										Load More Transactions
									</Button>
								</div>
							)}
						</div>
					</div>
				)}

				{/* Pagination Info - Only show when not on first page */}
				{currentPage > 1 && transactions.length > 0 && (
					<div className='flex justify-center items-center mt-4'>
						<div className='flex items-center space-x-4'>
							<Button
								variant='outline'
								size='sm'
								onClick={handlePrevPage}
								disabled={currentPage <= 1 || isLoading}
								className='text-pink-300 hover:text-white border-pink-900/50 hover:bg-pink-800/30'
							>
								<ChevronLeft className='h-4 w-4 mr-1' />
								Previous Page
							</Button>
							<span className='text-white/70 text-sm'>
								Page {currentPage}
							</span>
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	);
};
